import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, Square, Loader2, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTrainingStore } from '@/store/trainingStore';
import { cn } from '@/utils/cn';

export const TrainingSessionPage: React.FC = () => {
  useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const {
    activeSession,
    sessionMessages,
    sessionLoading,
    sendMessage,
    completeSession,
  } = useTrainingStore();

  const [inputMessage, setInputMessage] = useState('');
  const [isEnding, setIsEnding] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Redirect if no active session
  useEffect(() => {
    if (!activeSession) {
      navigate('/training');
    }
  }, [activeSession, navigate]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sessionMessages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputMessage.trim();
    if (!trimmed || sessionLoading) return;

    setInputMessage('');
    try {
      await sendMessage(trimmed);
    } catch {
      toast.error('Failed to send message');
    }
  };

  const handleEndSession = async () => {
    if (!confirm('End this training session? This cannot be undone.')) return;

    setIsEnding(true);
    try {
      await completeSession();
      toast.success('Training session completed');
      navigate('/training');
    } catch {
      toast.error('Failed to end session');
    } finally {
      setIsEnding(false);
    }
  };

  if (!activeSession) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Header Bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border-2 border-[#1a1a2e] px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">
              {activeSession.scenario_name || 'Training Session'}
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-violet-50 text-violet-700">
                <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                <span className="text-xs font-semibold">In Progress</span>
              </span>
              <span className="text-xs text-gray-400">
                {sessionMessages.length} message{sessionMessages.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
        <button
          onClick={handleEndSession}
          disabled={isEnding}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isEnding ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Ending...
            </>
          ) : (
            <>
              <Square className="w-4 h-4" />
              End Session
            </>
          )}
        </button>
      </div>

      {/* Chat Area */}
      <div className="bg-white rounded-xl border-2 border-[#1a1a2e] flex flex-col h-[calc(100vh-200px)]">
        {/* Message Feed */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {sessionMessages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Waiting for conversation to begin...</p>
              </div>
            </div>
          ) : (
            sessionMessages.map((msg, index) => {
              const isBot = msg.sender === 'bot';

              return (
                <div
                  key={index}
                  className={cn('flex', isBot ? 'justify-start' : 'justify-end')}
                >
                  <div
                    className={cn(
                      'max-w-[70%] rounded-lg p-3',
                      isBot
                        ? 'bg-gray-100 text-gray-900'
                        : 'bg-violet-500 text-white'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={cn(
                          'text-xs font-semibold',
                          isBot ? 'text-gray-500' : 'text-violet-100'
                        )}
                      >
                        {isBot ? 'Customer' : 'You'}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-[#1a1a2e] p-4">
          <form onSubmit={handleSendMessage} className="flex items-center gap-3">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your response..."
              disabled={sessionLoading}
              className="flex-1 px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              type="submit"
              disabled={sessionLoading || !inputMessage.trim()}
              className="inline-flex items-center justify-center w-10 h-10 bg-violet-500 text-white rounded-xl hover:bg-violet-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {sessionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TrainingSessionPage;
